import { Controller } from '@hotwired/stimulus'

// Resend button + countdown pair.
//
// The countdown mirrors the backend's RESEND_BUFFER. On every page render
// the server passes `initialRemaining` — the number of seconds before the
// backend will accept another send for this flow. If positive, the button
// shows disabled with that countdown; if zero, the button shows enabled.
// sessionStorage is also consulted so a click followed by a reload resumes
// the countdown without having to re-query the server.
export default class extends Controller {
  static targets = ["countdown", "text", "resend", "toast"]
  static values = {
    seconds: { type: Number, default: 60 },
    initialRemaining: { type: Number, default: 0 },
    key: { type: String, default: "resend-countdown-start" },
    url: String,
    email: String,
    redirect: String,
    paramKey: { type: String, default: "email" },
    one: String,
    other: String
  }

  connect() {
    const serverRemaining  = Math.max(0, this.initialRemainingValue || 0)
    const sessionRemaining = Math.max(0, Math.ceil(this.secondsValue - this.#elapsedSinceStart()))
    this.remaining = Math.max(serverRemaining, sessionRemaining)

    if (this.remaining > 0) {
      // Server wins when its remaining is greater than what sessionStorage
      // remembers (e.g. cold load, or the previous tab cleared storage).
      // Re-anchor sessionStorage so a subsequent reload picks up the
      // server-aligned start time instead of treating this as a fresh send.
      if (serverRemaining > sessionRemaining) {
        this.#writeStart(Date.now() - (this.secondsValue - serverRemaining) * 1000)
      }
      this.#hideResend()
      this.#startCountdown()
    } else {
      this.#showResend()
    }
  }

  #elapsedSinceStart() {
    const startTime = parseInt(this.#readStart(), 10)
    return isNaN(startTime) ? Infinity : (Date.now() - startTime) / 1000
  }

  #readStart() {
    try { return sessionStorage.getItem(this.keyValue) } catch { return null }
  }

  #writeStart(time = Date.now()) {
    try { sessionStorage.setItem(this.keyValue, time.toString()) } catch { /* private browsing */ }
  }

  #clearStart() {
    try { sessionStorage.removeItem(this.keyValue) } catch { /* private browsing */ }
  }

  disconnect() {
    this.#clearTimer()
    if (this.toastTimer) {
      clearTimeout(this.toastTimer)
      this.toastTimer = null
    }
  }

  resend(event) {
    event.preventDefault()
    if (this.remaining > 0) return

    // Optimistically lock the button — a clicker who holds the mouse
    // down shouldn't get multiple requests off while the network waits.
    this.remaining = this.secondsValue
    this.#writeStart()
    this.#hideResend()
    this.#startCountdown()

    this.#sendResendRequest().then(async (response) => {
      if (response && response.ok) {
        await this.#renderSuccess(response)
      } else {
        // Server rejected — release the lock so the user can retry.
        this.#clearTimer()
        this.#clearStart()
        this.remaining = 0
        this.#showResend()
      }
    })
  }

  // A bulk sender (e.g. the readiness board) returns a Turbo Stream that renders its
  // own toast with the real count; simple flows return no stream, so fall back to the
  // inline toast.
  async #renderSuccess(response) {
    const isStream = (response.headers.get("content-type") || "").includes("turbo-stream")
    if (isStream && window.Turbo) window.Turbo.renderStreamMessage(await response.text())
    else this.#showToast()
  }

  #sendResendRequest() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const body = new URLSearchParams()
    if (this.emailValue) body.append(this.paramKeyValue, this.emailValue)
    if (this.redirectValue) body.append("redirect_to", this.redirectValue)

    return fetch(this.urlValue, {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken,
        "Content-Type": "application/x-www-form-urlencoded",
        "Accept": "text/html"
      },
      body
    }).catch(() => null)
  }

  #startCountdown() {
    this.#updateDisplay()
    this.timer = setInterval(() => {
      this.remaining--

      if (this.remaining <= 0) {
        this.#clearTimer()
        this.#clearStart()
        this.#showResend()
      } else {
        this.#updateDisplay()
      }
    }, 1000)
  }

  #clearTimer() {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  #updateDisplay() {
    if (!this.hasTextTarget) return

    const template = this.remaining === 1 ? this.oneValue : this.otherValue
    this.textTarget.textContent = template.replace('%{count}', this.remaining)
  }

  #showResend() {
    if (this.hasCountdownTarget) this.countdownTarget.classList.add("hidden")
    if (this.hasResendTarget) this.resendTarget.classList.remove("hidden")
  }

  #hideResend() {
    if (this.hasCountdownTarget) this.countdownTarget.classList.remove("hidden")
    if (this.hasResendTarget) this.resendTarget.classList.add("hidden")
  }

  #showToast() {
    if (!this.hasToastTarget) return

    this.toastTarget.classList.remove("hidden")
    clearTimeout(this.toastTimer)
    this.toastTimer = setTimeout(() => {
      this.toastTarget.classList.add("hidden")
    }, 3000)
  }
}
